/**
 * Track Store — חנות מסלולי הלמידה
 *
 * State for any portal track (student / retirement / mortgage): topic tiles,
 * lesson detail, the topic-scoped chat and — on tracks that have one — the exam.
 *
 * Cost note: only `sendTopicMessage` costs money (it posts to the track chat
 * endpoint). Everything else reads pre-authored content or server-graded results,
 * so a user whose allowance is spent keeps full access to the content.
 *
 * ONE TRACK AT A TIME. The store holds the active track and clears its content
 * when it changes — otherwise navigating /pensia → /mashkanta would paint the
 * previous track's tiles for a frame before the new ones land.
 */

import { create } from 'zustand'
import { api, handleApiError } from '../services/api'
import { TRACKS, type TrackKey } from '../config/tracks'
import type {
  LearningTopicSummary,
  LearningTopicDetail,
  LearningProgressSummary,
  StudentAllowance,
  ExamAttempt,
  ExamResult,
  ChatMessage,
  TrackPublic,
} from '../types'

interface TopicsResponse {
  track: TrackPublic
  topics: LearningTopicSummary[]
  progress: LearningProgressSummary
  allowance: StudentAllowance | null
}

interface TopicDetailResponse {
  topic: LearningTopicDetail
  myProgress: { status: string; chatTurns: number; completedAt: string | null }
  conversationId: string | null
}

interface TrackSendResponse {
  conversation: { id: string; messageCount: number; lastMessage: string; topicSlug: string | null }
  response: string
  allowance: { capUSD: number; spentUSD: number; remainingUSD: number }
}

interface LearningState {
  activeTrack: TrackKey
  trackInfo: TrackPublic | null

  topics: LearningTopicSummary[]
  progress: LearningProgressSummary | null
  allowance: StudentAllowance | null
  topicsLoading: boolean
  topicsError: string | null

  topicDetail: LearningTopicDetail | null
  topicDetailLoading: boolean
  topicDetailError: string | null

  chatMessages: ChatMessage[]
  chatConversationId: string | null
  chatLoading: boolean
  chatError: string | null
  capReached: boolean
  /** Chips already used, so the same suggestion isn't offered twice. */
  usedSuggestions: string[]

  examAttempt: ExamAttempt | null
  examResult: ExamResult | null
  examLoading: boolean
  examError: string | null
  examLockedReason: { code: string; message: string; remainingTopics?: { slug: string; title: string }[] } | null

  fetchTopics: (track: TrackKey) => Promise<void>
  fetchTopic: (track: TrackKey, slug: string) => Promise<void>
  completeTopic: (track: TrackKey, slug: string) => Promise<void>
  loadTopicConversation: (track: TrackKey, slug: string) => Promise<void>
  sendTopicMessage: (track: TrackKey, slug: string | null, text: string) => Promise<void>
  startExam: (track: TrackKey) => Promise<void>
  submitExam: (track: TrackKey, answers: { questionId: string; selectedIndex: number }[]) => Promise<void>
  resetExam: () => void
  clearTopic: () => void
}

/** All track content lives under one base path, keyed by track. */
const apiBase = (track: TrackKey) => `/tracks/${track}`

function makeMessage(role: 'user' | 'assistant', content: string): ChatMessage {
  return {
    role,
    content,
    contentHash: '',
    timestamp: new Date().toISOString(),
    sanitized: role === 'assistant',
    flaggedForReview: false,
  }
}

const emptyTrackState = {
  trackInfo: null,
  topics: [] as LearningTopicSummary[],
  progress: null,
  allowance: null,
  topicDetail: null,
  chatMessages: [] as ChatMessage[],
  chatConversationId: null,
  chatError: null,
  capReached: false,
  usedSuggestions: [] as string[],
  examAttempt: null,
  examResult: null,
  examError: null,
  examLockedReason: null,
}

export const useLearningStore = create<LearningState>((set, get) => ({
  activeTrack: 'student',
  ...emptyTrackState,

  topicsLoading: false,
  topicsError: null,
  topicDetailLoading: false,
  topicDetailError: null,
  chatLoading: false,
  examLoading: false,

  fetchTopics: async (track) => {
    // Switching tracks must not show the previous track's tiles, progress or
    // allowance while the new request is in flight.
    const switching = get().activeTrack !== track
    set({
      activeTrack: track,
      ...(switching ? emptyTrackState : {}),
      topicsLoading: true,
      topicsError: null,
    })
    try {
      const response = await api.get<TopicsResponse>(`${apiBase(track)}/topics`)
      set({
        trackInfo: response.data.track ?? null,
        topics: response.data.topics,
        progress: response.data.progress,
        allowance: response.data.allowance,
        capReached: response.data.allowance?.capReached ?? false,
        topicsLoading: false,
      })
    } catch (error) {
      set({ topicsError: handleApiError(error), topicsLoading: false })
    }
  },

  fetchTopic: async (track, slug) => {
    set({
      activeTrack: track,
      topicDetailLoading: true,
      topicDetailError: null,
      topicDetail: null,
    })
    try {
      const response = await api.get<TopicDetailResponse>(`${apiBase(track)}/topics/${slug}`)
      set({
        topicDetail: response.data.topic,
        chatConversationId: response.data.conversationId,
        topicDetailLoading: false,
      })
    } catch (error) {
      set({ topicDetailError: handleApiError(error), topicDetailLoading: false })
    }
  },

  completeTopic: async (track, slug) => {
    try {
      const response = await api.post<{ progress: LearningProgressSummary }>(
        `${apiBase(track)}/topics/${slug}/complete`
      )
      set({ progress: response.data.progress })
      // Keep the tile list in step without a second round-trip.
      set({
        topics: get().topics.map((t) =>
          t.slug === slug ? { ...t, status: 'completed', completedAt: new Date().toISOString() } : t
        ),
      })
    } catch (error) {
      set({ topicsError: handleApiError(error) })
      throw error
    }
  },

  loadTopicConversation: async (track, slug) => {
    try {
      const response = await api.get<{
        conversation: { id: string } | null
        messages: ChatMessage[]
      }>(`${apiBase(track)}/topics/${slug}/conversation`)
      set({
        chatConversationId: response.data.conversation?.id ?? null,
        chatMessages: response.data.messages || [],
      })
    } catch {
      // A missing thread is not an error — they simply haven't asked yet.
      set({ chatMessages: [], chatConversationId: null })
    }
  },

  sendTopicMessage: async (track, slug, text) => {
    if (get().capReached) return
    set({
      chatLoading: true,
      chatError: null,
      chatMessages: [...get().chatMessages, makeMessage('user', text)],
      usedSuggestions: [...get().usedSuggestions, text],
    })

    try {
      const body: { message: string; topicSlug?: string; conversationId?: string } = {
        message: text,
      }
      if (slug) body.topicSlug = slug
      const existingId = get().chatConversationId
      if (existingId) body.conversationId = existingId

      const response = await api.post<TrackSendResponse>(
        `/chat/track/${track}/message`,
        body,
        { timeout: 120000 }
      )

      const { conversation, response: assistantResponse, allowance } = response.data
      set({
        chatConversationId: conversation.id,
        chatMessages: [...get().chatMessages, makeMessage('assistant', assistantResponse)],
        allowance: allowance
          ? { ...allowance, capReached: allowance.remainingUSD <= 0 }
          : get().allowance,
        chatLoading: false,
      })
    } catch (error) {
      const status = (error as any)?.response?.status
      const code = (error as any)?.response?.data?.code

      if (status === 402 || code === 'STUDENT_CAP_REACHED' || code === 'TRACK_CAP_REACHED') {
        set({
          capReached: true,
          chatLoading: false,
          chatMessages: [
            ...get().chatMessages,
            makeMessage('assistant', TRACKS[track].capReachedMessage),
          ],
        })
        return
      }

      // Drop the optimistic user message so the thread doesn't show a phantom turn.
      set({
        chatError: handleApiError(error),
        chatLoading: false,
        chatMessages: get().chatMessages.slice(0, -1),
      })
    }
  },

  startExam: async (track) => {
    if (!TRACKS[track].hasExam) return
    set({ examLoading: true, examError: null, examResult: null, examLockedReason: null })
    try {
      const response = await api.get<ExamAttempt>(`${apiBase(track)}/exam`)
      set({ examAttempt: response.data, examLoading: false })
    } catch (error) {
      const data = (error as any)?.response?.data
      if (data?.code === 'EXAM_LOCKED' || data?.code === 'EXAM_ATTEMPTS_EXHAUSTED' || data?.code === 'EXAM_BANK_EMPTY') {
        set({
          examLockedReason: {
            code: data.code,
            message: data.message,
            remainingTopics: data.remainingTopics,
          },
          examLoading: false,
        })
        return
      }
      set({ examError: handleApiError(error), examLoading: false })
    }
  },

  submitExam: async (track, answers) => {
    const attempt = get().examAttempt
    if (!attempt) return
    set({ examLoading: true, examError: null })
    try {
      const response = await api.post<ExamResult>(`${apiBase(track)}/exam/submit`, {
        attemptNumber: attempt.attemptNumber,
        answers,
      })
      set({ examResult: response.data, examAttempt: null, examLoading: false })
      // Refresh the hub numbers (best score, attempts left).
      await get().fetchTopics(track)
    } catch (error) {
      set({ examError: handleApiError(error), examLoading: false })
    }
  },

  resetExam: () => set({ examAttempt: null, examResult: null, examError: null, examLockedReason: null }),

  clearTopic: () =>
    set({
      topicDetail: null,
      chatMessages: [],
      chatConversationId: null,
      chatError: null,
      usedSuggestions: [],
    }),
}))
